/**
 * Pillar vocabulary (USER rule: pillars are PIECES of different widths,
 * heights and patterns — compose them relationally, don't repeat one
 * column). Dynamic families over one measured law, shared by finite
 * assembly, endless viaducts, and takeable spur decks.
 *
 * Measured kit facts (2026-08-09): every support segment is 0.5 tall
 * (tops 0.7 with the cradle at 0.5); footprints (w×d):
 *   four   bottom 1.0×1.0   middle 0.9×0.9   (4-leg tower)
 *   two    bottom 1.0×0.3   middle 0.9×0.2   (flat 2-leg frame)
 *   single bottom 0.6×0.6   middle 0.4×0.4   (slender post)
 *
 * RELATIONAL LAW — containment upward: a piece may stand on the piece
 * below only if its footprint fits inside it (ε 0.05). four carries
 * everything, two carries two, single carries single; single NEVER
 * stands on two (a 0.4-deep post on a 0.2-deep frame floats). `bottom`
 * segments are feet at ground contact AND transition collars mid-stack
 * — a collar only appears where its skirt obeys the same containment,
 * otherwise the kind change is a plain middle.
 *
 * Patterns are families over that law, drawn deterministically per
 * stack (position hash — the structural greedy stays rng-free, the
 * standing user rule): uniform in the seed's style kind, uniform in an
 * off kind, or tapered (a wide `four` base run, then a slender upper
 * run with a collar). New kinds = new table rows; the law is data.
 */
import type { Dir, Placement } from "../shared/types.ts";

export type SupportKind = "single" | "two" | "four";

interface Footprint {
  w: number;
  d: number;
}

const MIDDLE_FOOT: Record<SupportKind, Footprint> = {
  four: { w: 0.9, d: 0.9 },
  two: { w: 0.9, d: 0.2 },
  single: { w: 0.4, d: 0.4 },
};
const BOTTOM_FOOT: Record<SupportKind, Footprint> = {
  four: { w: 1.0, d: 1.0 },
  two: { w: 1.0, d: 0.3 },
  single: { w: 0.6, d: 0.6 },
};

/** Containment with authoring tolerance. */
export function fitsOn(above: Footprint, below: Footprint): boolean {
  return above.w <= below.w + 0.05 && above.d <= below.d + 0.05;
}

/** May a run of `upper` begin on top of a `lower` middle segment? */
export function mayStandOn(upper: SupportKind, lower: SupportKind): boolean {
  return fitsOn(MIDDLE_FOOT[upper], MIDDLE_FOOT[lower]);
}

/** Deterministic per-stack hash stream (no rng — position is the seed,
 * matching the tunnel-variant / island-jitter recipe). */
function stackHash(x: number, z: number, base: number): () => number {
  let h = Math.imul(Math.round(x * 8) | 0, 374761393) ^
    Math.imul(Math.round(z * 8) | 0, 668265263) ^
    Math.imul(Math.round(base * 8) | 0, 1274126177);
  return () => {
    h = Math.imul(h ^ (h >>> 13), 1103515245) + 12345;
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
  };
}

/** Shaft plan: the kind occupying each 0.5 slot, bottom → top.
 *
 * `extended` (M19c) admits two more patterns — LOOP MODE ONLY: the flag
 * is passed by finite-mode call sites (assemble !open, takeable). The
 * endless viaduct/spur stacks share this hash space, so an ungated
 * pattern change would shift endless goldens from a loop milestone; the
 * endless golden test is the tripwire. Both patterns obey the
 * containment law (narrower always ON wider — four never stands on the
 * thin two frame). */
export function planStack(
  bias: SupportKind,
  slots: number,
  draw: () => number,
  extended = false,
): SupportKind[] {
  if (slots <= 0) return [];
  const r = draw();
  if (extended && slots >= 4 && r < 0.18) {
    // Three-band taper: four → two → single, wide plinth to needle.
    const a = Math.max(1, Math.round(slots * (0.25 + draw() * 0.2)));
    const b = Math.max(1, Math.round((slots - a) * (0.4 + draw() * 0.2)));
    return [
      ...Array<SupportKind>(a).fill("four"),
      ...Array<SupportKind>(Math.min(b, slots - a - 1)).fill("two"),
      ...Array<SupportKind>(Math.max(1, slots - a - Math.min(b, slots - a - 1)))
        .fill("single"),
    ];
  }
  if (extended && slots >= 2 && r >= 0.18 && r < 0.34) {
    // Pedestal: one wide four course, then a slender column.
    const upper: SupportKind = draw() < 0.5 ? "two" : "single";
    return ["four", ...Array<SupportKind>(slots - 1).fill(upper)];
  }
  // Tapered tower: a wide `four` base run carrying a slender upper run.
  if (slots >= 3 && r < 0.3) {
    const upper: SupportKind = bias === "four"
      ? (draw() < 0.5 ? "two" : "single")
      : bias;
    const lowerLen = Math.max(
      1,
      Math.min(slots - 1, Math.round(slots * (0.35 + draw() * 0.3))),
    );
    return [
      ...Array<SupportKind>(lowerLen).fill("four"),
      ...Array<SupportKind>(slots - lowerLen).fill(upper),
    ];
  }
  // Uniform in an off kind — variety between stacks of one stretch.
  if (r < 0.5) {
    const others: SupportKind[] = (["four", "two", "single"] as const)
      .filter((k) => k !== bias);
    return Array(slots).fill(others[draw() < 0.5 ? 0 : 1]);
  }
  // Uniform in the seed's style kind (the dominant silhouette).
  return Array(slots).fill(bias);
}

/**
 * Compose one pillar into `placements`. `height` is the channel-base
 * height above `baseAt` (multiples of 0.5); cradle stacks put the
 * 0.7-tall top piece at height − 0.5 (its cradle lands exactly at the
 * channel base), plain columns end flush. The top piece takes the
 * uppermost shaft's kind so the arms grow from the right silhouette.
 */
export function emitSupportStack(
  placements: Placement[],
  prefix: string,
  bias: SupportKind,
  x: number,
  z: number,
  height: number,
  dir: Dir,
  cradle: boolean,
  baseAt = 0,
  /** M19c extended pattern pool — loop-mode call sites only (see
   * planStack: the endless golden test is the gate's tripwire). */
  extended = false,
) {
  const rotation = ((1 - dir + 4) % 4) as Dir;
  const draw = stackHash(x, z, baseAt);
  const slots = Math.max(0, Math.round((height - (cradle ? 0.5 : 0)) / 0.5));
  const plan = planStack(bias, slots, draw, extended);
  const piece = (kind: SupportKind, segment: string, y: number) => {
    placements.push({
      pieceId: `${prefix}-${kind}-${segment}`,
      pos: [x, y + baseAt, z],
      rotation,
      role: "support",
    });
  };
  for (let i = 0; i < plan.length; i++) {
    const kind = plan[i];
    const below = i > 0 ? plan[i - 1] : null;
    // Feet at contact; collars at kind changes when the skirt fits the
    // shaft below (the containment law) — else a plain middle.
    const collar = below !== null && below !== kind &&
      fitsOn(BOTTOM_FOOT[kind], MIDDLE_FOOT[below]);
    piece(
      kind,
      below === null ? "bottom" : collar ? "bottom" : "middle",
      i * 0.5,
    );
  }
  if (cradle && height >= 0.5 - 1e-9) {
    const topKind = plan.length > 0 ? plan[plan.length - 1] : bias;
    piece(topKind, "top", height - 0.5);
  }
}
